import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Mail, ShieldCheck, Zap, ArrowRight, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Heuristic: are we running on an Emergent preview URL? Google Cloud
// OAuth cannot whitelist dynamic preview subdomains, so the popup fails
// with "Access blocked: This app's request is invalid" every time.
// We use this to surface a clear explanation instead of a silent hang.
const IS_PREVIEW_ENV = typeof window !== 'undefined'
  && /preview\.emergentagent\.com$/i.test(window.location.hostname);

/**
 * Futuristic mandatory-connect Gmail gate.
 *
 * - Opens OAuth in a popup window (no navigating away from the task form the
 *   user is filling out).
 * - Polls `/api/gmail/status` every 2s while the popup is open. As soon as
 *   Gmail flips to `connected: true`, we auto-close the modal, notify the
 *   parent (`onConnected`), and the user stays on the same page.
 * - Non-dismissable: no "cancel" affordance. If the user closes the popup
 *   without finishing OAuth, the modal remains open — this is a hard
 *   requirement for task submission.
 */
export default function GmailGateModal({ open, onOpenChange, onConnected }) {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [justConnected, setJustConnected] = useState(false);
  const popupRef = useRef(null);
  const pollTimer = useRef(null);

  // Stop polling on unmount or when the modal closes
  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const startConnect = async () => {
    setError(null);
    setAttempted(true);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/gmail/auth-url`, { credentials: 'include' });
      const text = await r.text();
      let j;
      try { j = JSON.parse(text); } catch { j = {}; }
      if (!r.ok || !j.auth_url) {
        throw new Error(j.detail || `Could not initiate Gmail connect (HTTP ${r.status})`);
      }
      // Open Google OAuth in a centered popup window. If the popup is blocked,
      // we surface a message rather than yanking the user off the task form.
      const w = 480, h = 640;
      const y = window.top.outerHeight / 2 + window.top.screenY - h / 2;
      const x = window.top.outerWidth / 2 + window.top.screenX - w / 2;
      const popup = window.open(
        j.auth_url,
        'gmail_oauth',
        `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes`
      );
      if (!popup) {
        setError('Your browser blocked the sign-in popup. Please allow popups for this site and try again.');
        setLoading(false);
        return;
      }
      popupRef.current = popup;
      setConnecting(true);
      setLoading(false);
      // Poll every 2s while the popup is open. Stop when connected or when
      // the popup closes without a successful connection.
      pollTimer.current = setInterval(async () => {
        // If the user closed the popup, stop polling and re-enable the button.
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setConnecting(false);
          // Do a final status check — some browsers close the popup after a
          // successful redirect back to the app.
          try {
            const sr = await fetch(`${API_URL}/api/gmail/status`, { credentials: 'include' });
            const sj = await sr.json();
            if (sj.connected) {
              setJustConnected(true);
              setTimeout(() => {
                onConnected && onConnected();
                onOpenChange(false);
              }, 900);
              return;
            }
          } catch { /* ignore */ }
          // Popup closed without success. Surface a clear failure state so
          // the user isn't stuck on a silent "Waiting for Google" spinner.
          setError(
            IS_PREVIEW_ENV
              ? 'Google blocked the sign-in on this Preview URL (dynamic preview domains can\u2019t be whitelisted in Google Cloud). This flow works on your Production URL. You can also try again below.'
              : 'Sign-in was not completed. Please try again \u2014 make sure to finish the Google flow in the popup and allow the permissions.'
          );
          return;
        }
        try {
          const sr = await fetch(`${API_URL}/api/gmail/status`, { credentials: 'include' });
          if (!sr.ok) return;
          const sj = await sr.json();
          if (sj.connected) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
            setJustConnected(true);
            try { popupRef.current && popupRef.current.close(); } catch { /* cross-origin */ }
            setTimeout(() => {
              onConnected && onConnected();
              onOpenChange(false);
            }, 900);
          }
        } catch { /* poll again */ }
      }, 2000);
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    // Mandatory: block outside-click / Esc dismissal.
    <Dialog open={open} onOpenChange={(next) => { if (next) onOpenChange(next); }}>
      <DialogContent
        className="max-w-lg p-0 border-0 overflow-hidden bg-slate-950 text-slate-100 shadow-2xl [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="gmail-gate-modal"
      >
        <div className="relative">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgba(46,180,172,.35), transparent 45%), radial-gradient(circle at 80% 90%, rgba(120,80,255,.35), transparent 40%)',
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />

          <div className="relative px-8 pt-8 pb-6">
            <div className="flex items-center justify-center mb-5">
              <div className="relative">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transition-all ${
                  justConnected
                    ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-emerald-500/40'
                    : 'bg-gradient-to-br from-[#2EB4AC] via-cyan-500 to-violet-500 shadow-cyan-500/30'
                }`}>
                  {justConnected
                    ? <CheckCircle2 className="h-7 w-7 text-white" strokeWidth={2.4} />
                    : <Mail className="h-7 w-7 text-white" strokeWidth={2.2} />}
                </div>
                {!justConnected && <span className="absolute -inset-1 rounded-2xl ring-2 ring-cyan-400/40 animate-ping" />}
              </div>
            </div>

            <h2 className="text-center text-2xl font-bold tracking-tight mb-2">
              {justConnected ? 'You&rsquo;re connected!' : 'Connect Gmail to launch this task'}
            </h2>
            <p className="text-center text-sm text-slate-300 leading-relaxed">
              {justConnected ? (
                <>Perfect — your task form is now unlocked.</>
              ) : (
                <>
                  Task submission is unavailable until your Gmail is linked.
                  Briefs, notifications, and follow-ups are sent from{' '}
                  <span className="text-cyan-300 font-medium">your own address</span> so recipients recognise the sender.
                </>
              )}
            </p>

            {!justConnected && (
              <div className="grid grid-cols-3 gap-2 mt-6">
                {[
                  { Icon: Zap, label: 'Instant sends' },
                  { Icon: ShieldCheck, label: 'OAuth secure' },
                  { Icon: Mail, label: 'Your identity' },
                ].map(({ Icon, label }) => (
                  <div
                    key={label}
                    className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-3 flex flex-col items-center gap-1.5 backdrop-blur-sm"
                  >
                    <Icon className="h-4 w-4 text-cyan-300" />
                    <span className="text-[11px] font-medium text-slate-200 text-center leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            )}

            {!justConnected && IS_PREVIEW_ENV && !error && (
              <div
                className="mt-4 flex items-start gap-2 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
                data-testid="gmail-gate-preview-notice"
              >
                <Info className="h-3.5 w-3.5 mt-[1px] shrink-0" />
                <span>
                  You&rsquo;re on a Preview URL. Google OAuth only works on your Production URL &mdash;
                  if the popup blocks with &ldquo;Access blocked&rdquo;, that&rsquo;s expected here.
                </span>
              </div>
            )}

            {error && (
              <div
                className="mt-4 flex items-start gap-2 text-[12px] text-red-200 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                data-testid="gmail-gate-error"
              >
                <AlertTriangle className="h-4 w-4 mt-[1px] shrink-0 text-red-300" />
                <span className="leading-relaxed">{error}</span>
              </div>
            )}

            {!justConnected && (
              <>
                <button
                  type="button"
                  onClick={startConnect}
                  disabled={loading || connecting}
                  className="mt-6 w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-[#2EB4AC] via-cyan-500 to-violet-500 py-3.5 px-4 font-bold text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-shadow flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="gmail-gate-connect-btn"
                >
                  <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
                  {loading || connecting ? (
                    <>
                      <Loader2 className="relative h-4 w-4 animate-spin" />
                      <span className="relative">
                        {connecting ? 'Waiting for Google \u2014 a popup opened' : 'Opening Gmail sign-in'}
                      </span>
                    </>
                  ) : attempted && error ? (
                    <>
                      <RefreshCw className="relative h-4 w-4" />
                      <span className="relative">Try connecting again</span>
                    </>
                  ) : (
                    <>
                      <span className="relative">Connect Gmail securely</span>
                      <ArrowRight className="relative h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                {connecting && (
                  <p className="mt-3 text-center text-[11px] text-slate-400">
                    Finish sign-in in the popup — this dialog will unlock the moment we detect it.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
